import { useSelector, useDispatch } from "react-redux";
import { setUser, userLoggedOut } from "store/auth/userSlice";
import { clearSettingData } from "store/settings/settingsSlice";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import appConfig from "configs/app.config";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "apiServices/axiosInstance";
import { setThemeColor, setThemeColorLevel } from "store/theme/themeSlice";
import { themeConfig } from "configs/theme.config";
import { useEffect, useCallback } from "react";
import openNotification from "views/common/notification";
import { persistSilentAuthToken } from "utils/authToken";

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token, expired } = useSelector((state) => state?.auth?.session);
  const userData = useSelector((state) => state?.auth?.user?.userData);

  // ── Pure check — does NOT call handleSignOut ──────────────────────────────
  const isTokenValid = useCallback(() => {
    if (!token) return false;
    if (!expired) return false;
    return Number(expired) > new Date().getTime();
  }, [token, expired]);

  const onThemeColorChange = (value) => {
    dispatch(setThemeColor(value));
  };

  const onThemeColorLevelChange = (value) => {
    dispatch(setThemeColorLevel(value));
  };

  const handleSignOut = useCallback(() => {
    dispatch(onSignOutSuccess());
    dispatch(userLoggedOut());
    dispatch(clearSettingData());
    onThemeColorChange(themeConfig.themeColor);
    onThemeColorLevelChange(themeConfig.primaryColorLevel);
    navigate(appConfig.unAuthenticatedEntryPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate]);

  // ── Redirect freelancers who land on a non-profile page ──────────────────
  useEffect(() => {
    if (!isTokenValid()) return;

    let userRole = userData?.authority?.[0] || "";
    if (!userRole) {
      const storedRole = localStorage.getItem("role");
      if (storedRole) userRole = storedRole.toLowerCase();
    }

    const isFreelancer = userRole === "freelancer";
    const isProfilePage = location.pathname === appConfig.freelancerEntryPath;
    const isTransactionHistoryPage = location.pathname === `${appConfig.authenticatedEntryPath.replace('/welcome', '')}/gym/transaction-history`;
    const isSignInPage =
      location.pathname === appConfig.unAuthenticatedEntryPath;

    if (isFreelancer && !isProfilePage && !isSignInPage && !isTransactionHistoryPage) {
      console.log(
        "useAuth - Freelancer detected, redirecting to profile from:",
        location.pathname
      );
      navigate(appConfig.freelancerEntryPath, { replace: true });
    }
  }, [token, userData, location.pathname, navigate, isTokenValid]);

  // ── Google / Google-sign-in flow ──────────────────────────────────────────
  const signIn = useCallback(async ({ idToken, isSignUp = false, role: signInRole = "gymOwner" }) => {
    try {
      const response = await axiosInstance.post(
        "/client/v1/auth/new-google-sign-in",
        { idToken, isSignUp, role: signInRole },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.status) {
        const { token: respToken, data, gymProfile } = response;
        const role = String(data?.role || "").toLowerCase();

        localStorage.setItem(
          "userId",
          data?._id || data?.client_id || data?.trainer_id || ""
        );
        try {
          localStorage.setItem("auth_provider", "google");
          localStorage.setItem(
            "session",
            JSON.stringify({ token: respToken, user: data, gymProfile })
          );
          localStorage.setItem("role", data?.role);
          localStorage.setItem("user", JSON.stringify(data));
          localStorage.setItem("lastLoginResponse", JSON.stringify(response));
        } catch (e) {
          // ignore storage errors
        }

        openNotification(
          "success",
          response?.message || "Google authentication successful"
        );

        dispatch(onSignInSuccess(respToken));

        const hasGymProfile = Boolean(gymProfile);
        const isGymProfileComplete =
          hasGymProfile && Boolean(gymProfile?.gym_profile_status);

        let navigationPath;
        if (role === "freelancer") {
          navigationPath = appConfig.freelancerEntryPath;
        } else if (isGymProfileComplete) {
          navigationPath = appConfig.mainEntryPath;
        } else {
          navigationPath = appConfig.welcomeEntryPath;
        }

        dispatch(
          setUser({
            avatar:
              data?.avatar ||
              "https://static.vecteezy.com/system/resources/thumbnails/002/002/403/small/man-with-beard-avatar-character-isolated-icon-free-vector.jpg",
            userName:
              data?.client_name ||
              data?.trainer_name ||
              data?.businessName ||
              data?.name ||
              data?.full_name ||
              data?.client_email ||
              data?.trainer_email ||
              "Guest",
            email:
              data?.client_email ||
              data?.trainer_email ||
              "demo@gmail.com",
            phone_no:
              data?.phone_no || data?.trainer_phone_no || "+910000000000",
            authority:
              role === "freelancer"
                ? ["freelancer"]
                : isGymProfileComplete
                  ? role
                    ? [role]
                    : ["client"]
                  : ["welcome"],
            userId: data?.client_id || data?.trainer_id || 0,
            gym_logo: gymProfile?.gym_logo || "",
            gymId: data?.gym_id || 0,
          })
        );

        onThemeColorChange(
          hasGymProfile && gymProfile?.theme?.[0]
            ? gymProfile.theme[0]
            : themeConfig.themeColor
        );
        onThemeColorLevelChange(
          hasGymProfile && gymProfile?.theme?.[1]
            ? gymProfile.theme[1]
            : themeConfig.primaryColorLevel
        );

        navigate(navigationPath);
      } else {
        openNotification("danger", response?.message || "Google login failed");
        return response;
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors?.toString(),
      };
    }
  }, [dispatch, navigate]);

  // ── Email / OTP sign-in ───────────────────────────────────────────────────
  const signInWithEmailOtp = useCallback(async (verifyResponse, customToken = null) => {
    try {
      if (!verifyResponse?.status) {
        return {
          status: "failed",
          message: verifyResponse?.message || "OTP verify failed",
        };
      }

      const user = Array.isArray(verifyResponse?.data)
        ? verifyResponse.data[0]
        : verifyResponse?.data;

      if (!user) {
        return { status: "failed", message: "User data missing" };
      }

      const role = String(user?.role || "").toLowerCase();
      const gymProfile = verifyResponse?.gymProfile;

      // Use actual token if backend sends one, otherwise customToken, otherwise build a time-based one
      const sessionToken =
        verifyResponse?.token ||
        customToken ||
        "email-otp-session-" + new Date().getTime();

      persistSilentAuthToken(sessionToken);

      console.log("signInWithEmailOtp – token:", sessionToken, "| user:", user);

      // Persist before dispatching so sessionStorage/localStorage is ready
      try {
        localStorage.setItem("auth_provider", "email_otp");
        localStorage.setItem(
          "session",
          JSON.stringify({
            token: sessionToken,
            user,
            subscription: verifyResponse?.subscription || null,
          })
        );
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("role", user?.role || role);
        localStorage.setItem(
          "lastLoginResponse",
          JSON.stringify(verifyResponse)
        );
        localStorage.setItem(
          "userId",
          user?._id || user?.client_id || user?.trainer_id || user?.id || ""
        );
        localStorage.setItem(
          "id",
          user?._id || user?.client_id || user?.trainer_id || user?.id || ""
        );
      } catch (e) {
        // ignore storage errors
      }

      // Establish session in Redux — MUST happen before navigate() so
      // ProtectedRoute sees authenticated = true
      dispatch(onSignInSuccess(sessionToken));

      const hasGymProfile = Boolean(gymProfile);
      const isGymProfileComplete =
        hasGymProfile && Boolean(gymProfile?.gym_profile_status);

      let navigationPath;
      if (role === "freelancer") {
        navigationPath = appConfig.freelancerEntryPath;
      } else if (isGymProfileComplete) {
        navigationPath = appConfig.mainEntryPath;
      } else {
        navigationPath = appConfig.welcomeEntryPath;
      }

      dispatch(
        setUser({
          avatar:
            user?.avatar ||
            "https://static.vecteezy.com/system/resources/thumbnails/002/002/403/small/man-with-beard-avatar-character-isolated-icon-free-vector.jpg",
          userName:
            user?.client_name ||
            user?.trainer_name ||
            user?.businessName ||
            user?.name ||
            user?.full_name ||
            user?.client_email ||
            user?.trainer_email ||
            "Guest",
          email:
            user?.client_email || user?.trainer_email || "demo@gmail.com",
          phone_no: user?.phone_no || user?.trainer_phone_no || "+910000000000",
          authority:
            role === "freelancer"
              ? ["freelancer"]
              : isGymProfileComplete
                ? role
                  ? [role]
                  : ["client"]
                : ["welcome"],
          userId: user?.client_id || user?.trainer_id || 0,
          gym_logo: gymProfile?.gym_logo || "",
          gymId: user?.gym_id || 0,
        })
      );

      onThemeColorChange(
        hasGymProfile && gymProfile?.theme?.[0]
          ? gymProfile.theme[0]
          : themeConfig.themeColor
      );
      onThemeColorLevelChange(
        hasGymProfile && gymProfile?.theme?.[1]
          ? gymProfile.theme[1]
          : themeConfig.primaryColorLevel
      );

      navigate(navigationPath);
      return { status: "success" };
    } catch (e) {
      return { status: "failed", message: e?.message || "Unexpected error" };
    }
  }, [dispatch, navigate]);

  const signOut = useCallback(async () => {
    handleSignOut();
  }, [handleSignOut]);

  // ── checkAuthenticated — safe, no side-effects ───────────────────────────
  const checkAuthenticated = () => isTokenValid();

  return {
    authenticated: isTokenValid(),
    signIn,
    signInWithEmailOtp,
    signOut,
  };
}

export default useAuth;
